import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { tiposDe } from '../../componentes/formatos';
import { NOME_PRIORIDADE, duracaoTexto, isoDia, juntarDataHora, type Conflito, type Tecnico } from './comum';

interface ClienteBusca { id: string; razao_social: string; nome_fantasia: string | null }
interface LocalItem { id: string; nome: string; ativo: boolean }
interface TipoOs { id: string; nome: string; natureza: string }

export const DURACOES = [30, 60, 90, 120, 180, 240, 360, 480];

/** Chamado de onde a OS nasce: cliente fixo, local/descrição/prioridade sugeridos. */
export interface OrigemChamado { id: string; numero: string; cliente_id: string | null; cliente: string | null; local_id: string | null; descricao: string; prioridade: string }

/** Abrir OS. Com técnico + horário já sai agendada. */
export function FormOs({ tecnicos, preenchido, chamado, duracaoPadrao, aoSalvar, aoCancelar }: {
  tecnicos: Tecnico[];
  preenchido?: { responsavel_id?: string; data?: string; hora?: string };
  chamado?: OrigemChamado;
  duracaoPadrao: number;
  aoSalvar: (r: { id: string; numero: string; conflitos: Conflito[] }) => void;
  aoCancelar: () => void;
}) {
  const { usuario } = useSessao();
  const podeAgendar = pode(usuario, 'os.agendar');
  const [tipos, setTipos] = useState<TipoOs[]>([]);
  const [clienteId, setClienteId] = useState(chamado?.cliente_id ?? '');
  const [clienteNome, setClienteNome] = useState(chamado?.cliente ?? '');
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<ClienteBusca[]>([]);
  const [locais, setLocais] = useState<LocalItem[]>([]);
  const [f, setF] = useState({
    local_id: chamado?.local_id ?? '', tipo_os_id: '', prioridade: chamado?.prioridade ?? 'media', descricao: chamado?.descricao ?? '',
    agendar: Boolean(preenchido?.responsavel_id), responsavel_id: preenchido?.responsavel_id ?? '',
    data: preenchido?.data ?? isoDia(new Date()), hora: preenchido?.hora ?? '08:00', janela_min: String(duracaoPadrao),
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => { api<TipoOs[]>('/tipos-os').then(setTipos).catch(() => {}); }, []);
  useEffect(() => {
    if (!busca.trim() || clienteId) return setAchados([]);
    const t = setTimeout(() => api<{ itens: ClienteBusca[] }>(`/clientes?busca=${encodeURIComponent(busca)}`).then((r) => setAchados(r.itens.slice(0, 8))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [busca, clienteId]);
  useEffect(() => {
    if (!clienteId) return setLocais([]);
    api<{ locais: LocalItem[] }>(`/clientes/${clienteId}`).then((c) => {
      const ativos = c.locais.filter((l) => l.ativo);
      setLocais(ativos);
      if (ativos.length === 1 && !chamado?.local_id) setF((x) => ({ ...x, local_id: ativos[0]!.id }));
    }).catch(() => {});
  }, [clienteId]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!f.local_id) return setErro('Escolha o cliente e o local do atendimento.');
    if (f.agendar && !f.responsavel_id) return setErro('Escolha o técnico ou desmarque "Agendar agora".');
    setSalvando(true);
    try {
      const r = await api<{ id: string; numero: string; conflitos: Conflito[] }>('/os', { metodo: 'POST', corpo: {
        local_id: f.local_id, tipo_os_id: f.tipo_os_id, prioridade: f.prioridade, descricao: f.descricao,
        ...(chamado ? { chamado_id: chamado.id } : {}),
        ...(f.agendar ? { responsavel_id: f.responsavel_id, agendada_para: juntarDataHora(f.data, f.hora), janela_min: Number(f.janela_min) } : {}),
      } });
      aoSalvar(r);
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível abrir a OS.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      <div className="linha-2">
        <div className="campo"><span className="rotulo-campo">Cliente</span>
          {clienteId ? (
            <div className="campo-mascarado"><span>{clienteNome}</span>
              {!chamado?.cliente_id && <button type="button" className="btn-link" onClick={() => { setClienteId(''); setClienteNome(''); setF((x) => ({ ...x, local_id: '' })); }}>trocar</button>}</div>
          ) : (
            <div className="autocompletar">
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Digite o nome do cliente" autoFocus />
              {achados.length > 0 && <ul>{achados.map((c) => (
                <li key={c.id}><button type="button" onClick={() => { setClienteId(c.id); setClienteNome(c.nome_fantasia ?? c.razao_social); setBusca(''); }}>
                  <strong>{c.nome_fantasia ?? c.razao_social}</strong>{c.nome_fantasia && <small>{c.razao_social}</small>}</button></li>))}</ul>}
            </div>
          )}
        </div>
        <label>Parque
          <select required value={f.local_id} onChange={set('local_id')} disabled={!clienteId}>
            <option value="">{clienteId ? 'Escolha' : 'Escolha o cliente primeiro'}</option>
            {locais.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        </label>
      </div>
      <div className="linha-2">
        <label>Tipo
          <select required value={f.tipo_os_id} onChange={set('tipo_os_id')}>
            <option value="">Escolha</option>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </label>
        <label>Prioridade
          <select value={f.prioridade} onChange={set('prioridade')}>{tiposDe(NOME_PRIORIDADE).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
      </div>
      <label>O que foi pedido
        <textarea required minLength={5} maxLength={4000} rows={3} value={f.descricao} onChange={set('descricao')}
          placeholder='Ex.: "Portão de veículos da entrada não fecha; ronda está segurando manualmente."' />
      </label>
      {podeAgendar && (
        <label className="check">
          <input type="checkbox" checked={f.agendar} onChange={(e) => setF((x) => ({ ...x, agendar: e.target.checked }))} /> Agendar agora
        </label>
      )}
      {f.agendar && <CamposAgenda f={f} set={set} tecnicos={tecnicos} />}
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || !f.tipo_os_id || f.descricao.trim().length < 5}>{salvando ? 'Salvando…' : f.agendar ? 'Abrir e agendar' : 'Abrir OS'}</button>
      </div>
    </form>
  );
}

export function CamposAgenda({ f, set, tecnicos }: {
  f: { responsavel_id: string; data: string; hora: string; janela_min: string };
  set: (k: 'responsavel_id' | 'data' | 'hora' | 'janela_min') => (e: { target: { value: string } }) => void;
  tecnicos: Tecnico[];
}) {
  const duracoes = DURACOES.includes(Number(f.janela_min)) ? DURACOES : [...DURACOES, Number(f.janela_min)].sort((a, b) => a - b);
  return (
    <div className="bloco-agenda">
      <label>Técnico
        <select required value={f.responsavel_id} onChange={set('responsavel_id')}>
          <option value="">Escolha</option>
          {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nome}{t.placa ? ` · ${t.placa}` : ''}</option>)}
        </select>
      </label>
      <div className="linha-3-igual">
        <label>Dia<input type="date" required value={f.data} onChange={set('data')} /></label>
        <label>Hora<input type="time" required step={900} value={f.hora} onChange={set('hora')} /></label>
        <label>Duração prevista
          <select value={f.janela_min} onChange={set('janela_min')}>{duracoes.map((d) => <option key={d} value={d}>{duracaoTexto(d)}</option>)}</select>
        </label>
      </div>
    </div>
  );
}
